import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import React from 'react';
import ReactMarkdown from 'react-markdown';

import AppStoreImage from 'public/img/appstore.png';
import PlayStoreImage from 'public/img/playstore.png';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import { Chevron } from '@components/common/FormIcon';
import Accordion, { AccordionGroup } from '@components/parts/Accordion';
import { Container } from '@components/parts/Container';

import { MISO_APP_STORES } from '@utils/constants';
import { getVisibility } from '@utils/get-visibility';
import { groupBy } from '@utils/index';

import { colors } from '@styles/colors';

import { GlobalData } from '@customTypes/GlobalData';

const CustomAccordionHeader = ({ label, isOpen }: { label: string; isOpen: boolean }) => (
  <CustomHeaderWrapper>
    <CustomHeaderLabel>{label}</CustomHeaderLabel>
    <CustomHeaderIconWrapper isOpen={isOpen}>
      <Chevron color={colors.defaultBlack} />
    </CustomHeaderIconWrapper>
  </CustomHeaderWrapper>
);

const MobileAccordionContent = ({ menus }: { menus: any[] }) => (
  <MenuList>
    {menus.map((menu) => (
      <MenuItem key={menu.id}>
        <a href={menu.href}>{menu.name}</a>
      </MenuItem>
    ))}
  </MenuList>
);

type Props = {
  data: GlobalData;
  pageViewer: string;
};

const Footer: React.FC<Props> = (props) => {
  const router = useRouter();
  const { data, pageViewer } = props;
  const { footerMenus, footerCopyright, footerSocialMedia } = data;
  const isVisible = getVisibility(data.viewer_types, pageViewer);
  if (!isVisible) {
    return null;
  }

  const groupedFooterMenus = groupBy(footerMenus, 'group');

  const handleWtcFormClick = () => {
    router.push('/wtc-form');
  };

  return (
    <Wrapper>
      <Container>
        <WtcFormWrapper>
          <WtcFormRow>
            <WtcFormTitle>더 궁금한 점이 있나요?</WtcFormTitle>
            <WtcFormButton onClick={handleWtcFormClick}>문의하기</WtcFormButton>
          </WtcFormRow>
        </WtcFormWrapper>

        <GroupContainer>
          {Object.keys(groupedFooterMenus).map((group) => (
            <GroupWrapper key={group}>
              <DesktopView>
                <GroupTitleText>{group}</GroupTitleText>
                <MenuList>
                  {groupedFooterMenus[group].map((menu) => (
                    <MenuItem key={menu.id}>
                      <a href={menu.href}>{menu.name}</a>
                    </MenuItem>
                  ))}
                </MenuList>
              </DesktopView>

              <MobileView>
                <AccordionGroup allowMultiple={false}>
                  <Accordion
                    header={({ isOpen }) => <CustomAccordionHeader label={group} isOpen={isOpen} />}
                    wrapperStyle={{ backgroundColor: colors.gray80, border: 'none' }}
                    contentStyle={{ backgroundColor: colors.gray80 }}
                  >
                    <MobileAccordionContent menus={groupedFooterMenus[group]} />
                  </Accordion>
                </AccordionGroup>
              </MobileView>
            </GroupWrapper>
          ))}
          <AppWrapper>
            <Link href={MISO_APP_STORES.IOS}>
              <Image src={AppStoreImage} alt="appstore" width={130} height={40} unoptimized />
            </Link>
            <Link href={MISO_APP_STORES.ANDROID}>
              <Image src={PlayStoreImage} alt="playstore" width={136} height={40} unoptimized />
            </Link>
          </AppWrapper>
        </GroupContainer>
        <CopyRightContainer>
          <CopyRightTitle>유한회사 미소</CopyRightTitle>
          <CopyRightText>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {footerCopyright}
            </ReactMarkdown>
          </CopyRightText>
          <SocialMediaWrapper>
            {footerSocialMedia.map((socialMedia) => (
              <Link key={socialMedia.id} href={socialMedia.href} target="_blank" rel="noopener noreferrer">
                <Image
                  src={socialMedia.image!.data?.attributes.url}
                  alt={socialMedia.image!.data?.attributes.name}
                  width={24}
                  height={24}
                />
              </Link>
            ))}
          </SocialMediaWrapper>
          <CopyRightText>© 유한회사 미소. Miso, Inc. All Rights Reserved.</CopyRightText>
        </CopyRightContainer>
      </Container>
    </Wrapper>
  );
};

export default Footer;

const WtcFormWrapper = styled.div`
  width: 100%;
  padding-bottom: 60px;
  border-bottom: 1px solid #e3e5ea;
  @media (max-width: 768px) {
    padding-bottom: 30px;
  }
`;

const WtcFormRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafbfc;
  border-radius: 8px;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 24px;
  }
`;

const WtcFormTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.black};
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const WtcFormButton = styled.button`
  background: ${colors.blue};
  color: ${colors.white};
  font-size: 20px;
  font-weight: 700;
  border: none;
  border-radius: 4px;
  padding: 14px 0;
  min-width: 280px;
  text-align: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Wrapper = styled.div`
  padding: 60px 0;
  background: ${colors.gray80};
  border-top: 1px solid #e3e5ea;

  @media (max-width: 768px) {
    padding: 30px 0;
  }
`;

const GroupContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  border-bottom: 1px solid #e3e5ea;
  padding: 30px 0;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const GroupWrapper = styled.div`
  width: 100%;
`;

const GroupTitleText = styled.div`
  color: #2e3035;
  font-size: 14px;
  font-weight: 700;
`;

const DesktopView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileView = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const MenuList = styled.ul`
  display: flex;
  flex-direction: column;
  background-color: ${colors.gray80};
  gap: 12px;
`;

const MenuItem = styled.li`
  list-style-type: none;
  color: #62656a;
  background-color: ${colors.gray80};
  font-size: 14px;
  font-weight: 400;
`;

const AppWrapper = styled.div`
  display: flex;
  gap: 13px;

  @media (max-width: 768px) {
    margin-top: 15px;
  }
`;

const CopyRightContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 30px;
`;

const CopyRightTitle = styled.div`
  color: #2e3035;
  font-size: 14px;
  font-weight: 700;
`;

const CopyRightText = styled.div`
  color: #a3a7ae;
  font-size: 13px;
  font-weight: 400;
  line-height: 170%;
`;

const SocialMediaWrapper = styled.div`
  display: flex;
  gap: 14px;
`;

const CustomHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CustomHeaderLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CustomHeaderIconWrapper = styled.div<{ isOpen: boolean }>`
  margin-left: 12px;
  flex-shrink: 0;
  transition: transform 0.2s ease-in-out;
  transform: ${(props) => (props.isOpen ? 'rotate(90deg)' : 'rotate(0deg)')};
`;
